import React, { FC, useCallback, useState, Fragment } from 'react';
import { useRouter } from 'next/router';

import { Formik, Form } from 'formik';
import { FormLabel, FormControl, Button, Alert } from 'react-bootstrap';
import useTimeout from '@restart/hooks/useTimeout';
import { useAlert } from 'react-alert';

import ProductAction from '@views/components/product/ProductAction';
import styled from 'styled-components';
import { CONTENT } from '../intl';

import useLayoutContext from '@views/layouts/hooks/use-context';
import useBreakpoint, { Breakpoints } from '@hooks/use-break-point';

import { INTERNAL_ROUTES } from '@helpers/route';
import { DELAY } from '@helpers/const';
import _scrollToTop from '@helpers/scroll-top';
import _get from 'lodash/get';

import LikeButton from '@views/modules/common/components/like-button';

interface Values {
  quantity: number;
}

const initialValues: Values = {
  quantity: 1,
};

const ActionButton = styled(Button)`
  @media (min-width: 576px) {
    min-width: 180px;
    margin-right: 24px;
  }
`;

const ProductActionWrapper = styled(ProductAction)`
  display: flex;
  flex-wrap: wrap;
  @media (max-width: 576px) {
    position: fixed;
    bottom: 0;
    background: #fff;
    width: 100%;
    left: 0;
    z-index: 2147483645;
    flex-direction: column-reverse;
    padding: 6px 12px;
    border-top: 1px solid #e5e5e5;

    .btn {
      width: 100%;
    }
  }
`;

export const Action: FC<any> = ({ id }) => {
  const route = useRouter();
  const alert = useAlert();
  const isMobile = [Breakpoints.xs, Breakpoints.sm].includes(useBreakpoint());

  const {
    me,
    checkout: { handleAddItem },
    setActiveCartToast,
  } = useLayoutContext();
  const isAuthorized = !me.isEmpty();
  const [isRedirect, setRedirect] = useState<boolean>(false);

  const [isFetchError, setIsFetchError] = useState(false);
  const [fetchMessage, setFetchMessage] = useState(null);

  const timer = useTimeout();
  const hideErrorMessage = useCallback(() => {
    setFetchMessage(null);
    setIsFetchError(false);
  }, []);

  const cartToastTimer = useTimeout();

  const handleSuccess = useCallback(() => {
    if (isRedirect) {
      cartToastTimer.set(() => {
        route.push(INTERNAL_ROUTES.CART);
      }, 2000);

      return;
    }

    _scrollToTop();
    if (isMobile) {
      alert.info('Thêm vào giỏ hàng thành công');
    } else {
      setActiveCartToast(true);
      cartToastTimer.set(() => {
        setActiveCartToast(false);
      }, DELAY);
    }
  }, [isMobile, isRedirect, setActiveCartToast]);

  const onSubmit = async (values: Values, { setSubmitting }) => {
    const quantity = !!values.quantity ? values.quantity : 1;

    try {
      await handleAddItem({ id, quantity });
      setSubmitting(false);
      handleSuccess();
    } catch (error) {
      setSubmitting(false);
      setIsFetchError(true);
      setFetchMessage(
        _get(
          error,
          'message',
          _get(
            error,
            'graphQLErrors[0].message',
            'Có lỗi xảy ra vui lòng thử lại'
          )
        )
      );
      timer.set(hideErrorMessage, DELAY);
    }
  };

  const increaseQuantity = (quantity: number) => {
    return quantity + 1;
  };

  const decreaseQuantity = (quantity: number) => {
    const value = quantity - 1;

    return value < 1 ? 1 : value;
  };

  return (
    <Formik onSubmit={onSubmit} initialValues={initialValues}>
      {({
        values,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        submitForm,
      }) => (
        <Form onSubmit={handleSubmit} noValidate>
          <ProductAction className="mb-4">
            <ProductAction.Item>
              <div className="input-number product__quantity">
                <FormLabel className="input-number__label">
                  {CONTENT.QUANTITY}:
                </FormLabel>
                <Button
                  type="button"
                  className="input-number__sub"
                  onClick={() =>
                    setFieldValue('quantity', decreaseQuantity(values.quantity))
                  }
                />
                <FormControl
                  type="number"
                  name="quantity"
                  className="input-number__input"
                  value={`${values.quantity}`}
                  onChange={(event) => {
                    const value = parseInt(event.target.value);
                    setFieldValue('quantity', value <= 0 ? 1 : value);
                  }}
                />
                <Button
                  type="button"
                  className="input-number__add"
                  onClick={() =>
                    setFieldValue('quantity', increaseQuantity(values.quantity))
                  }
                />
              </div>
            </ProductAction.Item>
          </ProductAction>

          <ProductActionWrapper>
            <ProductAction.Item>
              <ActionButton
                type="button"
                variant="outline-primary"
                disabled={isSubmitting}
                size="lg"
                onClick={() => {
                  setRedirect(true);
                  submitForm();
                }}
              >
                {CONTENT.BUY}
              </ActionButton>
            </ProductAction.Item>
            <ProductAction.Item>
              <ActionButton
                type="submit"
                variant="primary"
                size="lg"
                disabled={isSubmitting}
              >
                {CONTENT.ADD_TO_CART}
              </ActionButton>
            </ProductAction.Item>
            <ProductAction.Item>
              <LikeButton isAuthorized={isAuthorized} productId={id} />
            </ProductAction.Item>
            {isFetchError && (
              <ProductAction.Item>
                <Alert variant="danger" onClose={hideErrorMessage}>
                  {fetchMessage}
                </Alert>
              </ProductAction.Item>
            )}
          </ProductActionWrapper>
        </Form>
      )}
    </Formik>
  );
};

export default Action;
